<script setup>
import ImageLazy from '@/components/ImageLazy.vue';
import { useRouter } from 'vue-router';
import { Heart, ImageOff, Eye, ExternalLink } from 'lucide-vue-next';
import { request } from '@/lib/api';
import { getUser } from '@/lib/user';

const router = useRouter();
const props = defineProps({
  project: Object,
  enablePreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['preview']);

function openProject(project) {
  const link = router.resolve({
    name: 'Design',
    params: {
      id: project.id,
    },
  });
  window.open(link.href, '_blank');
}

function handleCardClick(project) {
  if (props.enablePreview) {
    emit('preview', project);
  } else {
    openProject(project);
  }
}

async function likeProject(project) {
  const user = getUser();
  if (!user) return;
  await request('/api/project-interaction', {
    method: 'POST',
    body: JSON.stringify({
      projectId: project.id,
      actionType: 'like',
    }),
  });
  project.engagement.likes++;
}
</script>
<template>
  <div
    @click="handleCardClick(project)"
    class="group cursor-pointer border border-black/5 hover:border-black/10 transition-all duration-300 bg-[rgba(0,0,0,0.02)] rounded-xl overflow-hidden"
  >
    <div class="">
      <div class="relative aspect-[4/3] border-b p-2 border-black/5 overflow-hidden bg-transparent">
        <ImageLazy
          v-if="project.thumbnail"
          :src="project.thumbnail"
          :alt="project.name"
          height="100%"
          class="w-full h-full transition-transform duration-300 group-hover:scale-105"
          imageClass="w-full h-full object-contain"
        />
        <div
          v-else
          class="w-full h-full flex flex-col items-center justify-center bg-transparent transition-all duration-300"
        >
          <ImageOff class="w-10 h-10 text-black/40 mb-3" />
          <span class="text-black/40 text-sm font-medium">No Preview</span>
        </div>

        <!-- Like Button -->
        <button
          @click.stop="likeProject(project)"
          class="absolute z-20 top-2 left-2 size-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-all duration-200 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-105"
        >
          <Heart
            class="size-4"
            :class="project.liked ? 'text-rose-500 fill-rose-500' : 'text-black/60'"
          />
        </button>

        <!-- Open Button (visible on hover) -->
        <button
          @click.stop="openProject(project)"
          class="absolute z-20 top-2 right-2 size-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-all duration-200 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-105"
        >
          <ExternalLink class="size-4 text-black/60" />
        </button>
      </div>

      <div class="p-4">
        <div class="mb-1">
          <h3 class="text-sm font-normal text-black truncate leading-5">
            {{ project.name || 'Untitled Project' }}
          </h3>
        </div>

        <div class="flex items-center justify-between text-xs text-black/60">
          <router-link
            v-if="project.user"
            :to="{ name: 'Profile', params: { id: project.user.id } }"
            class="truncate max-w-[120px] hover:text-black transition-colors duration-200 hover:underline decoration-black/30 underline-offset-2 font-medium"
          >
            {{ project.user.name || project.user.username }}
          </router-link>

          <div class="flex items-center space-x-3">
            <div
              class="flex items-center space-x-1 text-black/40 hover:text-blue-500 transition-colors duration-200"
            >
              <Eye class="h-3.5 w-3.5" />
              <span class="text-xs font-medium">{{ project.engagement?.views || 0 }}</span>
            </div>

            <div
              class="flex items-center space-x-1 text-black/40 hover:text-rose-500 transition-colors duration-200 cursor-pointer"
              @click.stop="likeProject(project)"
            >
              <Heart
                class="h-3.5 w-3.5 transition-all duration-200"
                :class="project.liked ? 'fill-rose-500 text-rose-500' : ''"
              />
              <span class="text-xs font-medium" :class="project.liked ? 'text-rose-500' : ''">
                {{ project.engagement?.likes || 0 }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
